import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasPublicApi } from '~/services/kyClient';

import { type SupplierActivationRespondPayload } from './types';
import { zodSupplierActivationRespondPayloadSchema } from './validations';

/**
 * Submits a supplier's response (confirm or decline) to an activation request
 * via the magic link flow.
 *
 * This is a public endpoint — no authentication required. The `token` query
 * parameter acts as the authorization credential.
 *
 * @param params - An object containing:
 *   - reqId: Unique identifier of the supplier activation request.
 *   - token: One-time magic link token provided to the supplier for authorization.
 *   - payload: Supplier's response (confirm/decline) and optional reason for decline.
 * @param options - (Optional) Additional Ky request options, such as abort signal or custom headers.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers-(Public)/operation/post_supplier_activation_respond_api_v1_suppliers_actions__req_id__respond_post
 */
export const respondToSupplierActivation = async (
  {
    reqId,
    token,
    payload,
  }: {
    reqId: UUID;
    token: string;
    payload: SupplierActivationRespondPayload;
  },
  options?: KyRequestOptions,
) => {
  await vestigasPublicApi
    .post(ENDPOINT.SUPPLIER.RESPOND_ACTIVATION_PUBLIC(reqId), {
      ...options,
      json: payload,
      searchParams: { token },
      validate: {
        body: zodSupplierActivationRespondPayloadSchema,
      },
    })
    .json();
};
